/**
 * What New room does before there is a draft, shared by the computer's steps and the phone's and iPad's: a live's
 * form and the write that mints its draft, and a recording's start that hands the talk to the board it mints.
 */
import { useEffect, useRef, useState } from "react";
import { LIVE_FILE_MAX_BYTES, LIVE_LENGTHS, liveFileKind, notesEnough } from "../../shared/live-entry";
import { liveLongLabel, wallTimeToUtc } from "../../shared/live-time";
import { firstOn } from "../../shared/live-series";
import type { BoardStartLiveRequest, LibraryAsset } from "../../shared/types";
import { api, ApiError } from "../lib/api";
import {
  canStartLive,
  fileBase64,
  forgetEntry,
  keepEntry,
  type LiveDraft,
  recallEntry,
  repeatsProblem,
  startAt,
  startPassed,
} from "./liveForm";
import { handOffTalk, type PickedTalk } from "./pending";
import { followDay } from "./repeats";

export function lengthWords(min: number): string {
  return (LIVE_LENGTHS.find((l) => l.min === min)?.label ?? `${min} min`).replace(/\bmin\b/, "minutes");
}

/** "For 1 hour. Just once.": a repeat says itself under its own control, so only a one-off is named here. */
export function lengthLine(draft: LiveDraft): string {
  return `For ${lengthWords(draft.lengthMin)}.${draft.pattern ? "" : " Just once."}`;
}

/** A live's steps 1 and 2: when, and what it will cover. Write the page mints the draft. */
export function useLiveStart(onStarted: (webinarId: string) => void) {
  const [memory, setMemory] = useState(recallEntry);
  const [step, setStep] = useState<0 | 1>(0);
  const [file, setFile] = useState<LibraryAsset | null>(null);
  const [refused, setRefused] = useState("");
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const draft = memory.live;
  const set = (patch: Partial<LiveDraft>) =>
    setMemory((m) => {
      const live = { ...m.live, ...patch };
      if (patch.date && patch.pattern === undefined && live.pattern) live.pattern = followDay(live.pattern, m.live.date, patch.date);
      return { ...m, mode: "live", live };
    });
  useEffect(() => keepEntry(memory), [memory]);

  const at = startAt(draft);
  const passed = startPassed(draft);
  const problem = repeatsProblem(draft);
  const whenOk = !!at && !passed && !problem;
  const words = draft.notes.trim().split(/\s+/).filter(Boolean).length;
  // Why: a pattern that skips the day picked starts on its first day, so that is the day the summary names.
  const firstAt = draft.pattern && at ? (wallTimeToUtc(firstOn(draft.pattern, draft.date), draft.time, draft.timezone) ?? at) : at;
  const whenLine = firstAt ? liveLongLabel(firstAt, draft.timezone) : "";
  const short = !!words && !notesEnough(draft.notes);

  const take = (picked: LibraryAsset) => {
    if (!liveFileKind(picked.name, picked.mime) || !picked.url) {
      setRefused("That is not a file we can read. A PDF or a text file.");
      return;
    }
    if (picked.size_bytes > LIVE_FILE_MAX_BYTES) {
      setRefused("That file is bigger than 10 MB.");
      return;
    }
    setRefused("");
    setFile(picked);
  };

  const write = async () => {
    if (starting || !canStartLive(draft)) return;
    setStarting(true);
    setError("");
    try {
      const { date, time, timezone, lengthMin, notes, pattern } = draft;
      const body: BoardStartLiveRequest = { date, time, timezone, lengthMin, notes: notes.trim() };
      if (pattern) body.repeats = pattern;
      // The file is kept in Assets; its words go to the writer with the notes, as they always have.
      if (file?.url) {
        const got = await fetch(file.url, { credentials: "same-origin" });
        if (!got.ok) throw new ApiError(got.status, "That file could not be read. Pick it again.");
        body.file = { name: file.name, type: file.mime, data: await fileBase64(await got.blob()) };
      }
      const { webinarId } = await api.board.startLive(body);
      forgetEntry();
      onStarted(webinarId);
    } catch (e) {
      setStarting(false);
      setError(e instanceof ApiError ? e.message : "We could not start that. Try again.");
    }
  };

  return { draft, set, step, setStep, file, setFile, refused, take, starting, error, at, passed, problem, whenOk, whenLine, short, write };
}

/**
 * A recording's start: the pick mints the draft and hands the talk on. `from` is a talk already chosen in Assets,
 * started once.
 */
export function useRecordingStart(onStarted: (webinarId: string) => void, from?: string) {
  const [starting, setStarting] = useState(!!from);
  const [error, setError] = useState("");
  const once = useRef(false);
  const start = (talk: PickedTalk) => {
    setStarting(true);
    setError("");
    api.board
      .start()
      .then(({ webinarId }) => {
        handOffTalk(webinarId, talk);
        onStarted(webinarId);
      })
      .catch((e: unknown) => {
        setStarting(false);
        setError(e instanceof ApiError ? e.message : "We could not start that. Try again.");
      });
  };
  useEffect(() => {
    if (!from || once.current) return;
    once.current = true;
    start({ id: from, name: "", size: 0 });
    // Started once, for the talk the address names.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from]);
  return { starting, error, start };
}
